import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { getUser, getUserIntents } from "@/lib/api";
import { setShareMeta, backendOrigin } from "@/lib/meta";
import { fmtUSD, fmtUSDprecise, timeAgo, CHAIN_LABELS } from "@/lib/format";
import { CoinIcon } from "@/components/CoinIcon";
import { Wallet, Crown, Waves, Layers, HandCoins, Clock, CheckCircle2, XCircle } from "lucide-react";

const BADGE_ICONS = { whale: Waves, kingmaker: Crown, diversified: Layers, backer: HandCoins };

function IntentRow({ it }) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (it.status !== "pending") return;
    const tick = () => setRemaining(Math.max(0, Math.floor((new Date(it.expires_at).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [it.status, it.expires_at]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expired = it.status === "expired" || (it.status === "pending" && remaining <= 0);
  const status = it.status === "confirmed" ? "confirmed" : expired ? "expired" : "pending";
  const badge = {
    confirmed: { cls: "border-positive/30 bg-positive/[0.08] text-positive", Icon: CheckCircle2, label: "Credited" },
    expired: { cls: "border-negative/30 bg-negative/[0.06] text-negative", Icon: XCircle, label: "Expired" },
    pending: { cls: "border-gold/30 bg-gold/[0.06] text-gold", Icon: Clock, label: `${mm}:${ss}` },
  }[status];
  return (
    <div className="flex items-center justify-between border-b border-white/5 px-4 py-3" data-testid={`intent-row-${it.id}`}>
      <div className="flex items-center gap-3">
        <CoinIcon symbol={it.symbol} size={26} />
        <div>
          <Link to={it.entity_slug ? `/${it.entity_category === "callers" ? "caller" : "token"}/${it.entity_slug}` : "#"} className="font-display text-sm font-bold hover:text-gold">{it.entity_label}</Link>
          <div className="font-mono text-[11px] text-zinc-600">{it.exact_amount} {it.symbol} · {CHAIN_LABELS[it.chain] || it.chain}</div>
        </div>
      </div>
      <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-bold ${badge.cls}`}>
        <badge.Icon className="h-3.5 w-3.5" /> {badge.label}
      </span>
    </div>
  );
}

export default function UserProfile() {
  const { wallet } = useParams();
  const [data, setData] = useState(null);
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getUser(wallet).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    getUserIntents(wallet).then(setIntents).catch(() => setIntents([]));
  }, [wallet]);

  useEffect(() => {
    if (!data) return;
    setShareMeta({
      title: `Backer ${data.ens || data.wallet_short} · #${data.contributor_rank ?? "?"} on FLIP`,
      description: `This wallet has backed ${fmtUSD(data.total_contributed)} across ${data.tokens_supported} tokens and ${data.callers_supported} callers on FLIP — every dollar verified on-chain.`,
      image: `${backendOrigin()}/api/og/board.png`,
      url: `${window.location.origin}/user/${data.wallet_address}`,
      type: "profile",
    });
  }, [data]);

  if (loading) {
    return (<div className="min-h-screen"><Navbar /><div className="mx-auto max-w-4xl px-4 py-16"><div className="h-40 animate-pulse rounded-2xl border border-white/5 bg-white/[0.02]" /></div></div>);
  }

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#080808] p-6" data-testid="user-header">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-800 to-zinc-900">
            <Wallet className="h-7 w-7 text-zinc-400" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display text-2xl font-black tracking-tight">{data.ens || data.wallet_short}</div>
            <div className="break-all font-mono text-xs text-zinc-600">{data.wallet_address}</div>
            {data.contributor_rank && (
              <div className="mt-1 font-mono text-sm text-gold">Backer rank #{data.contributor_rank}</div>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Total contributed" value={fmtUSD(data.total_contributed)} gold />
          <Stat label="Tokens supported" value={data.tokens_supported} />
          <Stat label="KOLs supported" value={data.callers_supported} />
          <Stat label="Largest contribution" value={fmtUSD(data.largest_contribution)} positive />
        </div>

        {data.badges.length > 0 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#080808] p-5" data-testid="user-badges">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Badges</div>
            <div className="flex flex-wrap gap-2">
              {data.badges.map((b) => {
                const Icon = BADGE_ICONS[b.key] || HandCoins;
                return (
                  <span key={b.key} className="flex items-center gap-2 rounded-full border border-gold/25 bg-gold/[0.06] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-gold">
                    <Icon className="h-3.5 w-3.5" strokeWidth={2} /> {b.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {intents.length > 0 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#080808]" data-testid="user-intents">
            <div className="border-b border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">Payment requests</div>
            {intents.map((it) => <IntentRow key={it.id} it={it} />)}
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-white/10 bg-[#080808]" data-testid="user-activity">
          <div className="border-b border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">Recent activity</div>
          {data.recent_activity.map((a, i) => (
            <Link to={a.entity_slug ? (a.payment_token_symbol === "USDC" ? `/caller/${a.entity_slug}` : `/token/${a.entity_slug}`) : "#"} key={i} className="flex items-center justify-between border-b border-white/5 px-4 py-3 hover:bg-white/[0.03]">
              <div>
                <div className="font-display text-sm font-bold">{a.entity_name}</div>
                <div className="text-[11px] text-zinc-600">{timeAgo(a.created_at)}</div>
              </div>
              <span className="font-mono text-sm font-bold text-positive">{fmtUSDprecise(a.usd_value)}</span>
            </Link>
          ))}
          {data.recent_activity.length === 0 && <div className="px-4 py-12 text-center text-zinc-600">No contributions yet</div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, gold, positive }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">{label}</div>
      <div className={`font-mono text-xl font-bold ${gold ? "text-gold" : positive ? "text-positive" : "text-white"}`}>{value}</div>
    </div>
  );
}
